"use client";

import { useRouter } from "next/navigation";
import Link from "next/link";
import { useEffect, useId, useRef, useState, useTransition } from "react";
import {
  deleteBase,
  setPlaceBase,
  updateBase,
} from "@/app/actions/companion-bases";
import {
  setShallowSearchParams,
  useShallowSearchParam,
} from "@/lib/hooks/use-shallow-search-param";
import type { CompanionBaseWithAnchor } from "@/lib/companion/bases-server";
import type { CompanionSignupIntentPrefill } from "@/lib/companion/signup-intent";
import { stayEntryHref } from "@/lib/companion/stay-navigation";

export type CompanionBaseSheetModel = {
  bases: CompanionBaseWithAnchor[];
  activeBase: CompanionBaseWithAnchor | null;
  viewedBase: CompanionBaseWithAnchor | null;
  baseChipLabel: string;
  baseOverrideMessage: string | null;
  noActiveBasePrompt: string | null;
  needsBaseMigration: boolean;
  assignBasePrompt: {
    baseId: string;
    baseName: string;
    count: number;
    placeIds: string[];
  } | null;
};

type Props = {
  model: CompanionBaseSheetModel;
  intentPrefill: CompanionSignupIntentPrefill | null;
  triggerTone?: "dark" | "light";
  triggerLabel?: string;
  navigationMode?: "shallow" | "route";
};

const BASE_SHEET_TONE_CLASSES = {
  dark: {
    trigger:
      "inline-flex min-h-[44px] items-center justify-center rounded-full border border-white/14 bg-white/8 px-4 py-2 text-[12px] font-semibold text-white hover:border-gold/40 hover:bg-white/12",
    notice:
      "rounded-sm border border-white/12 bg-white/8 px-3 py-2 text-[12.5px] leading-relaxed text-white/78",
    overrideAction: "font-semibold text-gold-light hover:text-white",
    migration:
      "block w-full rounded-sm border border-gold/30 bg-gold/10 px-3 py-3 text-left text-[12.5px] font-semibold leading-relaxed text-gold-light hover:bg-gold/15",
    assignment:
      "rounded-sm border border-emerald-200/30 bg-emerald-100/10 px-3 py-3 text-[12.5px] leading-relaxed text-white/82",
    assignmentAction:
      "mt-2 inline-flex min-h-[44px] items-center justify-center rounded-sm bg-white px-3 py-2 text-[12px] font-semibold text-navy hover:bg-cream disabled:cursor-wait",
  },
  light: {
    trigger:
      "inline-flex min-h-[44px] items-center justify-center rounded-full border border-border bg-white px-4 py-2 text-[12px] font-semibold text-navy hover:border-teal/50",
    notice:
      "rounded-sm border border-border bg-white px-3 py-2 text-[12.5px] leading-relaxed text-navy",
    overrideAction: "font-semibold text-teal-dark hover:text-navy",
    migration:
      "block w-full rounded-sm border border-gold/40 bg-gold/10 px-3 py-3 text-left text-[12.5px] font-semibold leading-relaxed text-navy hover:bg-gold/15",
    assignment:
      "rounded-sm border border-emerald-200 bg-emerald-50 px-3 py-3 text-[12.5px] leading-relaxed text-navy",
    assignmentAction:
      "mt-2 inline-flex min-h-[44px] items-center justify-center rounded-sm bg-navy px-3 py-2 text-[12px] font-semibold text-white hover:bg-teal-dark disabled:cursor-wait",
  },
} as const;

export function CompanionBaseSheet({
  model,
  intentPrefill,
  triggerTone = "dark",
  triggerLabel,
  navigationMode = "shallow",
}: Props) {
  const router = useRouter();
  const toneClasses = BASE_SHEET_TONE_CLASSES[triggerTone];
  const [open, setOpen] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const headingId = useId();
  const addHref = stayEntryHref({
    action: "new",
    place:
      intentPrefill?.suggestedSpots[0]?.slug ??
      intentPrefill?.region?.anchorSlug ??
      null,
  });
  useEffect(() => {
    if (!open) return;
    const sheet = dialog.current;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    sheet?.showModal();
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      sheet?.close();
      document.body.style.overflow = previous;
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, [open]);
  const [message, setMessage] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();

  const viewedBaseId =
    useShallowSearchParam("base") ?? model.viewedBase?.id ?? null;
  useEffect(() => {
    const handleOpenBaseSheet = () => setOpen(true);
    window.addEventListener("companion:open-base-sheet", handleOpenBaseSheet);
    return () =>
      window.removeEventListener(
        "companion:open-base-sheet",
        handleOpenBaseSheet,
      );
  }, []);

  function setViewedBase(baseId: string | null) {
    if (navigationMode === "shallow") {
      setShallowSearchParams({ base: baseId });
      return;
    }

    const url = new URL(window.location.href);
    if (baseId) url.searchParams.set("base", baseId);
    else url.searchParams.delete("base");
    router.replace(`${url.pathname}${url.search}`, { scroll: false });
  }

  return (
    <>
      <div className="mt-5 space-y-3">
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={open}
          className={toneClasses.trigger}
        >
          {triggerLabel ?? model.baseChipLabel}
        </button>
        {model.baseOverrideMessage ? (
          <div className={toneClasses.notice}>
            {model.baseOverrideMessage}{" "}
            <button
              type="button"
              onClick={() => setViewedBase(null)}
              className={toneClasses.overrideAction}
            >
              Back to active
            </button>
          </div>
        ) : null}
        {model.noActiveBasePrompt ? (
          <div className={toneClasses.notice}>{model.noActiveBasePrompt}</div>
        ) : null}
        {model.needsBaseMigration ? (
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={open}
            className={toneClasses.migration}
          >
            New: set your base so the verdict knows where you are. Choose a
            holiday, a season or local life.
          </button>
        ) : null}
        {model.assignBasePrompt ? (
          <div className={toneClasses.assignment}>
            <p className="font-semibold">
              Put your {model.assignBasePrompt.count} saved{" "}
              {model.assignBasePrompt.count === 1 ? "place" : "places"} under{" "}
              {model.assignBasePrompt.baseName}?
            </p>
            <button
              type="button"
              disabled={pending}
              onClick={() => {
                startTransition(async () => {
                  for (const placeId of model.assignBasePrompt?.placeIds ??
                    []) {
                    await setPlaceBase(
                      placeId,
                      model.assignBasePrompt?.baseId ?? null,
                    );
                  }
                  router.refresh();
                });
              }}
              className={toneClasses.assignmentAction}
            >
              {pending ? "Assigning" : "Assign now"}
            </button>
          </div>
        ) : null}
      </div>

      {open ? (
        <dialog
          ref={dialog}
          aria-labelledby={headingId}
          onCancel={() => setOpen(false)}
          onClick={(event) => {
            if (event.target === event.currentTarget) setOpen(false);
          }}
          className="fixed inset-0 m-0 h-dvh max-h-none w-full max-w-none border-0 bg-transparent p-0 text-navy backdrop:bg-navy/65 backdrop:backdrop-blur-sm"
        >
          <div className="absolute inset-x-0 bottom-0 mx-auto max-h-[88vh] max-w-[720px] overflow-y-auto rounded-t-[18px] bg-white p-4 shadow-[0_-24px_80px_rgba(0,0,0,0.28)] md:bottom-6 md:rounded-[18px]">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[1.6px] text-gold">
                  Companion bases
                </p>
                <h2
                  id={headingId}
                  className="mt-1 font-heading text-[28px] font-bold leading-tight text-navy"
                >
                  Homes and trips
                </h2>
                <Link
                  href="/companion/plan/trips"
                  onClick={() => setOpen(false)}
                  className="inline-flex min-h-11 items-center text-sm font-semibold text-teal underline"
                >
                  Manage trips and stays
                </Link>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="inline-flex h-11 w-11 items-center justify-center rounded-full text-text-muted hover:bg-warm-bg"
                aria-label="Close bases"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>

            {message ? (
              <p className="mt-3 rounded-sm border border-border-light bg-warm-bg px-3 py-2 text-[12.5px] text-navy">
                {message}
              </p>
            ) : null}

            <div className="mt-4 space-y-3">
              {model.bases.length > 0 ? (
                model.bases.map((base) => (
                  <BaseRow
                    key={base.id}
                    base={base}
                    activeBaseId={model.activeBase?.id ?? null}
                    viewedBaseId={viewedBaseId}
                    pending={pending}
                    onEdit={() => setOpen(false)}
                    onView={setViewedBase}
                    onDelete={(baseId) => {
                      startTransition(async () => {
                        const result = await deleteBase(baseId);
                        setMessage(
                          result.success
                            ? "Base removed."
                            : (result.error ?? "Unable to remove that base."),
                        );
                        if (result.success) {
                          if (viewedBaseId === baseId) {
                            setViewedBase(null);
                          }
                          router.refresh();
                        }
                      });
                    }}
                    onMakePrimary={(baseId) => {
                      startTransition(async () => {
                        const result = await updateBase(baseId, {
                          isPrimary: true,
                        });
                        setMessage(
                          result.success
                            ? "Primary home updated."
                            : (result.error ??
                                "Unable to update the primary home."),
                        );
                        if (result.success) router.refresh();
                      });
                    }}
                  />
                ))
              ) : (
                <p className="rounded-sm border border-border-light bg-warm-bg px-3 py-3 text-[12.5px] leading-relaxed text-text-muted">
                  Add a home base or a trip and PeakWave will start scoping your
                  saved places around it.
                </p>
              )}
            </div>

            <div className="mt-5 rounded-md border border-border-light bg-warm-bg p-4">
              <h3 className="font-heading text-lg font-bold text-navy">
                Add your next stay
              </h3>
              <p className="mt-2 text-sm leading-6 text-text-muted">
                A holiday, a working season or local life. Choose your
                destination and dates, then add arrival details whenever you
                need them. Device drafts stay in Trips and stays.
              </p>
              <Link
                href={addHref}
                onClick={() => setOpen(false)}
                className="mt-3 inline-flex min-h-11 items-center justify-center rounded-sm bg-teal px-4 py-2 text-sm font-semibold text-white hover:bg-teal-dark"
              >
                Add a stay
              </Link>
            </div>
          </div>
        </dialog>
      ) : null}
    </>
  );
}

function BaseRow({
  base,
  activeBaseId,
  viewedBaseId,
  pending,
  onView,
  onDelete,
  onMakePrimary,
  onEdit,
}: {
  base: CompanionBaseWithAnchor;
  activeBaseId: string | null;
  viewedBaseId: string | null;
  pending: boolean;
  onView: (baseId: string | null) => void;
  onDelete: (baseId: string) => void;
  onMakePrimary: (baseId: string) => void;
  onEdit: () => void;
}) {
  const isViewed = viewedBaseId === base.id;

  return (
    <div className="rounded-sm border border-border-light bg-white px-3 py-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-[14px] font-semibold text-navy">{base.name}</p>
          <p className="mt-1 text-[12px] text-text-muted">
            {base.kind === "home" ? "Home" : "Trip"}
            {base.id === activeBaseId ? " · Active now" : ""}
            {base.kind === "home" && base.isPrimary ? " · Primary" : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onView(isViewed ? null : base.id)}
            className="inline-flex min-h-[44px] items-center justify-center rounded-sm border border-border px-3 py-2 text-[12px] font-semibold text-navy hover:border-gold/50"
          >
            {isViewed ? "Use active view" : "View base"}
          </button>
          <Link
            href={stayEntryHref({ action: "edit", id: base.id })}
            onClick={onEdit}
            className="inline-flex min-h-11 items-center justify-center rounded-sm border border-border px-3 py-2 text-xs font-semibold text-navy hover:border-teal"
          >
            Edit stay
          </Link>
          {base.kind === "home" && !base.isPrimary ? (
            <button
              type="button"
              disabled={pending}
              onClick={() => onMakePrimary(base.id)}
              className="inline-flex min-h-[44px] items-center justify-center rounded-sm border border-border px-3 py-2 text-[12px] font-semibold text-navy hover:border-gold/50 disabled:cursor-wait"
            >
              Make primary
            </button>
          ) : null}
          <button
            type="button"
            disabled={pending}
            onClick={() => onDelete(base.id)}
            className="inline-flex min-h-[44px] items-center justify-center rounded-sm border border-rose-200 px-3 py-2 text-[12px] font-semibold text-rose-700 hover:bg-rose-50 disabled:cursor-wait"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}
